import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Checkbox } from "antd";
import { CloseCircleOutlined } from "@ant-design/icons";
import "./ProductFilterPanel.css";

/**
 * Build a tree from AllProductCategories data.
 * Each root category has printArray: { parentId: [children] }
 */
const buildCategoryTree = (allCategories) => {
  if (!allCategories || allCategories.length === 0) return [];

  const treeRoots = [];
  allCategories.forEach((rootCat) => {
    const printArray = rootCat.printArray || {};

    const buildNode = (category) => {
      const children = printArray[category.mProductCategoryId] || [];
      return {
        key: category.mProductCategoryId,
        title: category.name || category.value || "Unknown",
        children: children.map((child) => buildNode(child)),
      };
    };

    treeRoots.push(buildNode(rootCat));
  });

  return treeRoots;
};

/**
 * Prune tree: keep only nodes that have products (directly or via descendants).
 */
const pruneEmptyCategories = (nodes, categoryCounts) => {
  return nodes
    .map((node) => {
      const prunedChildren = node.children ? pruneEmptyCategories(node.children, categoryCounts) : [];
      const directCount = categoryCounts[node.key] || 0;
      const hasChildrenWithProducts = prunedChildren.length > 0;
      if (directCount > 0 || hasChildrenWithProducts) {
        return { ...node, children: prunedChildren };
      }
      return null;
    })
    .filter(Boolean);
};

/**
 * Collect all descendant IDs of a node (including itself).
 */
const expandCategoryWithDescendants = (node) => {
  const ids = [node.key];
  if (node.children) {
    node.children.forEach((child) => {
      ids.push(...expandCategoryWithDescendants(child));
    });
  }
  return ids;
};

/**
 * Build a flat name lookup from allProductCategories + categoriesData.
 */
export const buildCategoryNameMap = (allProductCategories, categoriesData) => {
  const names = {};
  (categoriesData || []).forEach((c) => {
    names[c.mProductCategoryId] = c.name;
  });
  (allProductCategories || []).forEach((root) => {
    names[root.mProductCategoryId] = root.name;
    const printArray = root.printArray || {};
    Object.values(printArray)
      .flat()
      .forEach((child) => {
        names[child.mProductCategoryId] = child.name;
      });
  });
  return names;
};

/** Recursive tree node */
const CategoryTreeNode = ({ node, depth, selectedCategories, categoryCounts, onToggle }) => {
  const [expanded, setExpanded] = useState(depth < 1);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedCategories.includes(node.key);
  const count = categoryCounts[node.key] || 0;

  return (
    <div>
      <div className={`category-tree-node${isSelected ? " selected" : ""}`}>
        {hasChildren ? (
          <span
            className={`category-tree-expand${expanded ? " expanded" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            ▶
          </span>
        ) : (
          <span style={{ width: 16, flexShrink: 0 }} />
        )}
        <span className="category-tree-label" onClick={() => onToggle(node)}>
          {node.title}
        </span>
        <span className="category-tree-count">{count}</span>
      </div>
      {hasChildren && expanded && (
        <div className="category-tree-children">
          {node.children.map((child) => (
            <CategoryTreeNode
              key={child.key}
              node={child}
              depth={depth + 1}
              selectedCategories={selectedCategories}
              categoryCounts={categoryCounts}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/** Collapsible section wrapper */
const FilterSection = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="filter-section">
      <div className="filter-section-header" onClick={() => setOpen(!open)}>
        <span className="filter-section-title">{title}</span>
        <span className={`filter-section-toggle${!open ? " collapsed" : ""}`}>▼</span>
      </div>
      {open && <div className="filter-section-body">{children}</div>}
    </div>
  );
};

const ProductFilterPanel = ({
  allProductCategories,
  categoriesData,
  brandsData,
  productsList,
  dbCategoryCounts,
  selectedCategories,
  selectedBrands,
  priceRange,
  onCategoryChange,
  onBrandChange,
  onPriceRangeChange,
  onClearAll,
}) => {
  const [brandSearch, setBrandSearch] = useState("");
  const [priceMin, setPriceMin] = useState(priceRange?.[0] ?? "");
  const [priceMax, setPriceMax] = useState(priceRange?.[1] ?? "");

  // Sync local price input state when parent priceRange changes (e.g., after Clear All).
  // Without this, Clear All resets priceRange in parent but inputs still show old values.
  useEffect(() => {
    setPriceMin(priceRange?.[0] ?? "");
    setPriceMax(priceRange?.[1] ?? "");
  }, [priceRange]);

  // Build tree from AllProductCategories; fallback to flat categoriesData
  const categoryTree = useMemo(() => {
    const tree = buildCategoryTree(allProductCategories);
    if (tree.length > 0) return tree;
    return (categoriesData || []).map((cat) => ({
      key: cat.mProductCategoryId,
      title: cat.name || cat.value,
      children: [],
    }));
  }, [allProductCategories, categoriesData]);

  // Dynamic cross-filter counts.
  // When no brand/price filters are active, use DB-level counts (covers ALL products)
  // so the initial view shows correct totals before the full list is lazy-loaded.
  const { categoryCounts, brandCounts } = useMemo(() => {
    const [pMin, pMax] = priceRange || [null, null];
    const hasBrandFilter = selectedBrands.length > 0;
    const hasPriceFilter = (pMin != null && pMin !== "") || (pMax != null && pMax !== "");

    // Category counts: if no brand/price filter active, use pre-computed DB counts
    if (!hasBrandFilter && !hasPriceFilter && dbCategoryCounts && Object.keys(dbCategoryCounts).length > 0) {
      const brdCounts = {};
      const matchesCategory = selectedCategories.length === 0;
      (productsList || []).forEach((product) => {
        if (matchesCategory || selectedCategories.includes(product.mProductCategoryId)) {
          const brandId = product.brandId || "unknown";
          brdCounts[brandId] = (brdCounts[brandId] || 0) + 1;
        }
      });
      return { categoryCounts: dbCategoryCounts, brandCounts: brdCounts };
    }

    const catCounts = {};
    const brdCounts = {};

    (productsList || []).forEach((product) => {
      const price = product.sunitprice || 0;
      const matchesPrice =
        (pMin == null || pMin === "" || price >= pMin) && (pMax == null || pMax === "" || price <= pMax);

      const matchesBrand = !hasBrandFilter || selectedBrands.includes(product.brandId);
      if (matchesBrand && matchesPrice) {
        const catId = product.mProductCategoryId || "uncategorized";
        catCounts[catId] = (catCounts[catId] || 0) + 1;
      }

      const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(product.mProductCategoryId);
      if (matchesCategory && matchesPrice) {
        const brandId = product.brandId || "unknown";
        brdCounts[brandId] = (brdCounts[brandId] || 0) + 1;
      }
    });

    return { categoryCounts: catCounts, brandCounts: brdCounts };
  }, [productsList, dbCategoryCounts, selectedBrands, selectedCategories, priceRange]);

  const handleCategoryToggle = useCallback(
    (node) => {
      const allDescendants = expandCategoryWithDescendants(node);
      const isSelected = selectedCategories.includes(node.key);
      let newSelected;
      if (isSelected) {
        const removeSet = new Set(allDescendants);
        newSelected = selectedCategories.filter((id) => !removeSet.has(id));
      } else {
        const addSet = new Set([...selectedCategories, ...allDescendants]);
        newSelected = [...addSet];
      }
      onCategoryChange(newSelected);
    },
    [selectedCategories, onCategoryChange]
  );

  const handleBrandToggle = useCallback(
    (brandId) => {
      const isSelected = selectedBrands.includes(brandId);
      const newSelected = isSelected ? selectedBrands.filter((id) => id !== brandId) : [...selectedBrands, brandId];
      onBrandChange(newSelected);
    },
    [selectedBrands, onBrandChange]
  );

  const handlePriceApply = useCallback(() => {
    onPriceRangeChange([priceMin === "" ? null : Number(priceMin), priceMax === "" ? null : Number(priceMax)]);
  }, [priceMin, priceMax, onPriceRangeChange]);

  const filteredBrands = useMemo(() => {
    // Only show brands that have at least 1 product matching current filters
    const withProducts = (brandsData || []).filter((b) => (brandCounts[b.brandId] || 0) > 0);
    if (!brandSearch) return withProducts;
    const term = brandSearch.toLowerCase();
    return withProducts.filter((b) => b.name?.toLowerCase().includes(term));
  }, [brandsData, brandSearch, brandCounts]);

  return (
    <div className="product-filter-panel">
      {/* Categories */}
      <FilterSection title="Categories">
        <div
          className={`category-tree-node${selectedCategories.length === 0 ? " selected" : ""}`}
          onClick={() => onCategoryChange([])}
        >
          <span style={{ width: 16, flexShrink: 0 }} />
          <span className="category-tree-label">All Categories</span>
          <span className="category-tree-count">{(productsList || []).length}</span>
        </div>
        {pruneEmptyCategories(categoryTree, categoryCounts).map((node) => (
          <CategoryTreeNode
            key={node.key}
            node={node}
            depth={0}
            selectedCategories={selectedCategories}
            categoryCounts={categoryCounts}
            onToggle={handleCategoryToggle}
          />
        ))}
      </FilterSection>

      {/* Brands */}
      {filteredBrands.length > 0 || brandSearch ? (
        <FilterSection title="Brands">
          <div className="brand-search">
            <input type="text" placeholder="Search brands..." value={brandSearch} onChange={(e) => setBrandSearch(e.target.value)} />
          </div>
          {filteredBrands.length === 0 && brandSearch && (
            <div style={{ padding: "6px 2px", fontSize: 12, color: "#999" }}>No brands found</div>
          )}
          {filteredBrands.map((brand) => (
            <div key={brand.brandId} className="brand-item" onClick={() => handleBrandToggle(brand.brandId)}>
              <Checkbox checked={selectedBrands.includes(brand.brandId)} />
              <span className="brand-item-name">{brand.name}</span>
              <span className="brand-item-count">{brandCounts[brand.brandId] || 0}</span>
            </div>
          ))}
        </FilterSection>
      ) : null}

      {/* Price Range */}
      <FilterSection title="Price Range" defaultOpen={false}>
        <div className="price-range-inputs">
          <input
            type="number"
            className="price-range-input"
            placeholder="Min"
            value={priceMin}
            onChange={(e) => setPriceMin(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handlePriceApply()}
          />
          <span className="price-range-separator">—</span>
          <input
            type="number"
            className="price-range-input"
            placeholder="Max"
            value={priceMax}
            onChange={(e) => setPriceMax(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handlePriceApply()}
          />
        </div>
        <button className="price-range-apply" onClick={handlePriceApply}>
          Apply
        </button>
      </FilterSection>
    </div>
  );
};

/**
 * Active filter chips bar — placed above the product grid.
 */
export const ActiveFiltersBar = ({
  selectedCategories,
  selectedBrands,
  priceRange,
  allProductCategories,
  categoriesData,
  brandsData,
  onRemoveCategory,
  onRemoveBrand,
  onClearPriceRange,
  onClearAll,
}) => {
  const hasFilters =
    selectedCategories.length > 0 ||
    selectedBrands.length > 0 ||
    (priceRange && (priceRange[0] != null || priceRange[1] != null));

  const categoryNames = useMemo(() => buildCategoryNameMap(allProductCategories, categoriesData), [allProductCategories, categoriesData]);

  const brandNames = useMemo(() => {
    const names = {};
    (brandsData || []).forEach((b) => {
      names[b.brandId] = b.name;
    });
    return names;
  }, [brandsData]);

  if (!hasFilters) return null;

  return (
    <div className="active-filters-bar">
      {selectedCategories.map((catId) => (
        <span key={catId} className="active-filter-chip">
          {categoryNames[catId] || catId}
          <CloseCircleOutlined className="active-filter-chip-remove" onClick={() => onRemoveCategory(catId)} />
        </span>
      ))}
      {selectedBrands.map((brandId) => (
        <span key={brandId} className="active-filter-chip">
          {brandNames[brandId] || brandId}
          <CloseCircleOutlined className="active-filter-chip-remove" onClick={() => onRemoveBrand(brandId)} />
        </span>
      ))}
      {priceRange && (priceRange[0] != null || priceRange[1] != null) && (
        <span className="active-filter-chip">
          {priceRange[0] != null ? priceRange[0] : "0"} — {priceRange[1] != null ? priceRange[1] : "∞"}
          <CloseCircleOutlined className="active-filter-chip-remove" onClick={onClearPriceRange} />
        </span>
      )}
      <button className="clear-all-filters" onClick={onClearAll}>
        Clear All
      </button>
    </div>
  );
};

export default React.memo(ProductFilterPanel);
